import { getPatientPurchaseEligibility } from "@/api/va-order-page/orderTest";
import store from "@/store";

/**
 * Single entry point for /orderTest/patientPurchaseEligibility.
 * Makes one API call per patient, handles both response fields, and
 * commits VA panel visibility directly to the store:
 *   - item_eligibility: disables genetics tests already purchased
 *   - va_eligibility: determines VA panel visibility
 *
 * @param {Array}  bfsTestList - Full BFS test node list to update in-place
 * @param {Object} userInfo    - Current user/clinic info from Vuex state
 */
export async function handlePatientPurchaseEligibility(bfsTestList, userInfo) {
  // Reset genetics duplicate flags and set isNotAllowedSelect flags
  bfsTestList.forEach(node => {
    if (node.value.isGeneticDuplicate) {
      node.value.isNotAllowedSelect = false;
      node.value.isNotAllowedSelectForShortcut = false;
      node.value.isNotAllowedSelectForXChooseY = false;
    }
    node.value.isGeneticDuplicate = false;
  });

  const hideVa = userInfo?.beta_programs?.includes("hide_va");
  const patient = JSON.parse(sessionStorage.getItem("patient") || "null");
  const isTestPriceCalculator = window.location.href.includes("/testpricecalculator");

  // No patient or test price calculator: skip API, VA visibility determined by hide_va alone
  if (!patient?.id || isTestPriceCalculator) {
    store.dispatch("orderTest/tests/setVaPanelVisibility", { isHidden: hideVa });
    return;
  }

  try {
    const { data } = await getPatientPurchaseEligibility({ patient_id: patient.id });

    // Process item_eligibility — disable already-purchased genetics tests
    if (data?.item_eligibility) {
      sessionStorage.setItem("oneTimePurchaseEligibility", JSON.stringify({
        patientId: patient.id,
        data: data.item_eligibility,
        timestamp: Date.now()
      }));
      Object.entries(data.item_eligibility).forEach(([testIdStr, isEligible]) => {
        if (isEligible === false) {
          const testId = parseInt(testIdStr);
          bfsTestList
            .filter(node => node.id === testId)
            .forEach(node => {
              node.value.isNotAllowedSelect = true;
              node.value.isNotAllowedSelectForShortcut = true;
              node.value.isNotAllowedSelectForXChooseY = true;
              node.value.isGeneticDuplicate = true;
            });
        }
      });
    }

    // Process va_eligibility — clinic with hide_va defers to patient history
    const showVaPanel = !hideVa || (data?.va_eligibility ?? true);
    store.dispatch("orderTest/tests/setVaPanelVisibility", { isHidden: !showVaPanel });
  } catch (err) {
    console.log("getPatientPurchaseEligibility error", err);
    store.dispatch("orderTest/tests/setVaPanelVisibility", { isHidden: false }); // fail open
  }
}

/**
 * Get one-time purchase eligibility data from sessionStorage cache
 * This is kept for other components that may need the raw data
 * @returns {Object} - Eligibility data object { testId: boolean }
 */
export function getOneTimePurchaseEligibility() {
  try {
    const cachedEligibility = sessionStorage.getItem("oneTimePurchaseEligibility");
    if (cachedEligibility) {
      const parsed = JSON.parse(cachedEligibility);
      const currentPatient = JSON.parse(sessionStorage.getItem("patient") || "{}");
      const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in ms

      if (currentPatient && parsed.patientId === currentPatient.id &&
        (Date.now() - parsed.timestamp) < CACHE_DURATION) {
        return parsed.data;
      } else {
        // Clear stale cache
        sessionStorage.removeItem("oneTimePurchaseEligibility");
        return {};
      }
    }
    return {};
  } catch (error) {
    console.error("Error reading eligibility from sessionStorage:", error);
    return {};
  }
}